import React, { useEffect, useRef, useState } from 'react';

import { MtxButton } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { generatePath, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useGetApiArbitrageDetail } from '@/api/arbitrage/arbitrage';
import { AllocationAnalysis } from '@/components/AllocationAnalysis/AllocationAnalysis';
import ArbitrageCardsSection from '@/components/ArbitrageCardsSection/ArbitrageCardsSection';
import ArbitrageQpsFlow from '@/components/ArbitrageQpsFlow/ArbitrageQpsFlow';
import ArbitrageSummaryRecap from '@/components/ArbitrageSummaryRecap/ArbitrageSummaryRecap';
import ExpiredProposalModal from '@/components/ExpiredProposalModal/ExpiredProposalModal';
import FixedActionsBar from '@/components/FixedActionsBar/FixedActionsBar';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { Spinner } from '@/components/Spinner/Spinner';
import { SummaryWalletArbitrage } from '@/components/SummaryWalletArbitrage/SummaryWalletArbitrage';
import Tabs from '@/components/Tabs/Tabs';
import TunnelResultPage from '@/components/TunnelResultPage/TunnelResultPage';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { ROUTE } from '@/constants/route';
import { ARBITRAGE_STATUS } from '@/enums/arbitrageStatus';
import useArbitrageQps from '@/hooks/useArbitrageQps';
import useArbitrageSummaryData from '@/hooks/useArbitrageSummaryData';
import useCheckArbitrageSignerIdentity from '@/hooks/useCheckArbitrageSignerIdentity';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ContentModalDeclineArbitrage } from '@/modals/ContentModalDeclineArbitrage/ContentModalDeclineArbitrage';
import { ContentModalValidateArbitrage } from '@/modals/ContentModalValidateArbitrage/ContentModalValidateArbitrage';
import { useUserStore } from '@/store/useStore';

import styles from './styles.module.css';

type ArbitrageSummaryLocationState = {
  arbitrageId: string;
  idPlacement: string;
};

const ArbitrageSummary: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { windowWidth } = useScreenSize();
  const locationState = useLocation().state as ArbitrageSummaryLocationState | null;
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [universignError, setUniversignError] = useState(false);
  const [isQpsOpen, setIsQpsOpen] = useState(false);
  const userConseiller = useUserStore((state) => state.user);

  const { data, isLoading, isError, refetch } = useGetApiArbitrageDetail(id ?? '');

  const supports = data?.supports;
  const qps = useArbitrageQps(id ?? '', supports?.supportsInvestis);
  const checkSignerIdentity = useCheckArbitrageSignerIdentity(
    data?.numContrat ?? undefined,
    data?.placementId ?? undefined
  );

  const isValidateLoading = checkSignerIdentity.isChecking || checkSignerIdentity.isPending;

  const { riskAligned, totalReinvested, exitFees, entryFees, totalFees } = useArbitrageSummaryData(data);

  const beforeCategories = data?.actualAllocation;
  const afterCategories = data?.newAllocation;

  // Garde de ré-entrance : empêche un double envoi de l'accept (action financière) sur double-clic/tap rapide,
  // au cas où la mise à jour du `disabled` traîne derrière un second événement. Couvre les deux chemins
  // (validation directe + bouton "Continuer" du modal) qui passent tous par cette fonction.
  const isSubmittingRef = useRef(false);
  const hasTrackedRef = useRef(false);

  const runAccept = async () => {
    if (!id || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      const success = await checkSignerIdentity.proceed(id);
      setUniversignError(!success);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onContinueFromValidateModal = async () => {
    setShowValidateModal(false);
    if (qps.isRequired) {
      setIsQpsOpen(true);
      return;
    }
    await runAccept();
  };

  const onClickValidate = async () => {
    if (riskAligned) {
      await onContinueFromValidateModal();
    } else {
      setShowValidateModal(true);
    }
  };

  useEffect(() => {
    if (
      hasTrackedRef.current ||
      !locationState?.arbitrageId ||
      !locationState?.idPlacement ||
      isError ||
      isLoading ||
      data?.status !== ARBITRAGE_STATUS.WAITING_CLIENT_VALIDATION ||
      universignError ||
      checkSignerIdentity.checkError
    )
      return;
    hasTrackedRef.current = true;
    TRACKING_INSTANCE.setTcVars({
      page_nom: 'proposition_d_arbitrage',
      page_template: 'espace client',
    });
    void TRACKING_INSTANCE.triggerEvent('page', undefined, {
      event_id: 'page',
      tunnel_produit: 'tunnel_unique',
      event_categorie: 'affichage espace client',
      event_nom: 'proposition_d_arbitrage',
      event_page: 'proposition_d_arbitrage',
    });
  }, [
    locationState?.arbitrageId,
    locationState?.idPlacement,
    isError,
    isLoading,
    data?.status,
    universignError,
    checkSignerIdentity.checkError,
  ]);

  if (!locationState?.arbitrageId || !locationState?.idPlacement) return <Navigate to={ROUTE.ERREUR} replace />;
  if (isError) return <Navigate to={ROUTE.ERREUR} replace />;
  if (isLoading) return <Loader />;

  if (data?.status !== ARBITRAGE_STATUS.WAITING_CLIENT_VALIDATION) {
    return <ExpiredProposalModal onClose={() => navigate(ROUTE.DASHBOARD)} />;
  }

  // Erreur Universign traitée en premier : sinon la garde signerData ci-dessous resterait bloquée
  // en Loader (checkError laisse signerData undefined).
  if (universignError || checkSignerIdentity.checkError) {
    const onRetry = () => {
      setUniversignError(false);
      if (checkSignerIdentity.checkError) checkSignerIdentity.refetch();
    };

    return (
      <TunnelResultPage
        iconName="warning-solide"
        iconVariant="error"
        title={t('universignCheckError.title')}
        subtitles={[t('universignCheckError.subtitle')]}
        ctaLabel={t('universignCheckError.cta')}
        onCtaClick={onRetry}
      />
    );
  }

  // On n'affiche la page (et donc le bouton "Valider") qu'une fois signerData disponible : garantit
  // que le bouton n'est jamais rendu sans signerData.
  if (!checkSignerIdentity.signerData) return <Loader />;

  // Placé après les gardes : le questionnaire remplace le récapitulatif et sa barre d'actions,
  // il ne se superpose pas à eux.
  if (isQpsOpen) {
    const summaryPath = generatePath(ROUTE.ARBITRAGESUMMARY, { id: id ?? '' });

    return (
      <ArbitrageQpsFlow
        entries={qps.entries}
        unavailableProductName={qps.unavailableProductName}
        start={qps.start}
        isAcceptLoading={isValidateLoading}
        breadcrumbItems={[
          { label: t('arbitrageSummary.breadcrumbLabel'), path: summaryPath },
          { label: t('qps.breadcrumbLabel'), path: summaryPath },
        ]}
        onExit={() => setIsQpsOpen(false)}
        onRetryLoad={() => void refetch()}
        onAllPassed={() => void runAccept()}
      />
    );
  }

  return (
    <div>
      {showDeclineModal && (
        <ModaleDefaut
          content={
            <ContentModalDeclineArbitrage
              arbitrageId={locationState.arbitrageId}
              placementId={locationState.idPlacement}
              onReviewProposal={() => setShowDeclineModal(false)}
            />
          }
          size="large"
          onClickClose={() => setShowDeclineModal(false)}
          mobileStyle="stick-to-bottom"
        />
      )}

      {showValidateModal && (
        <ModaleDefaut
          content={
            <ContentModalValidateArbitrage
              onReviewProposal={() => setShowValidateModal(false)}
              onContinueFromValidateModal={onContinueFromValidateModal}
            />
          }
          size="large"
          onClickClose={() => setShowValidateModal(false)}
          mobileStyle="stick-to-bottom"
        />
      )}

      <h1 className={styles.arbitrageSummaryTitle}>{t('summaryArbitrage.pageTitle')}</h1>
      {windowWidth > BREAKPOINTS.mobile ? (
        <>
          <AllocationAnalysis
            currentScore={supports?.currentSrri ?? 0}
            newScore={supports?.newSrri ?? 0}
            currentProfile={
              supports?.currentInvestProfil ?? t('summaryArbitrage.allocationAnalysis.profileDefaultValue')
            }
            newProfile={supports?.newInvestProfil ?? t('summaryArbitrage.allocationAnalysis.profileDefaultValue')}
            contractName={data?.Name ?? ''}
            totalReinvested={totalReinvested}
            totalFees={totalFees}
          />
          <div className={styles.supportsSection}>
            <h2>{t('summaryArbitrage.detailSupports')}</h2>
            <ArbitrageCardsSection
              supportsDesinvestis={supports?.supportsDesinvestis ?? []}
              supportsInvestis={supports?.supportsInvestis ?? []}
            />
            <div className={styles.arbitrageSummaryWallet}>
              <SummaryWalletArbitrage
                exitFees={exitFees}
                entryFees={entryFees}
                beforeCategories={beforeCategories}
                afterCategories={afterCategories}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={styles.arbitrageSummaryMobileContainer}>
          <Tabs
            tabs={[
              {
                label: t('summaryArbitrage.detailSupports'),
                disabled: false,
                content: (
                  <section className={styles.supportsSectionMobile}>
                    <ArbitrageCardsSection
                      supportsDesinvestis={supports?.supportsDesinvestis ?? []}
                      supportsInvestis={supports?.supportsInvestis ?? []}
                    />
                  </section>
                ),
              },
              {
                label: t('summaryArbitrage.tabRecap'),
                disabled: false,
                content: (
                  <div className={styles.summarySectionMobile}>
                    <ArbitrageSummaryRecap
                      currentScore={supports?.currentSrri ?? 0}
                      newScore={supports?.newSrri ?? 0}
                      currentProfile={
                        supports?.currentInvestProfil ?? t('summaryArbitrage.allocationAnalysis.profileDefaultValue')
                      }
                      newProfile={
                        supports?.newInvestProfil ?? t('summaryArbitrage.allocationAnalysis.profileDefaultValue')
                      }
                      contractName={data?.Name ?? ''}
                      totalReinvested={totalReinvested}
                      totalFees={totalFees}
                      exitFees={exitFees}
                      entryFees={entryFees}
                      beforeCategories={beforeCategories}
                      afterCategories={afterCategories}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
      <FixedActionsBar centered>
        <MtxButton
          className={styles.fixedActionsBarButton}
          variant="tertiary"
          size="large"
          fullWidth
          onClick={() => {
            if (!isValidateLoading) {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'placement',
                event_nom: 'refuser',
              });
              setShowDeclineModal(true);
            }
          }}
          aria-disabled={isValidateLoading}
          disabled={userConseiller?.advisorView ?? false}
        >
          <span>{t('common.declineButton')}</span>
        </MtxButton>
        <MtxButton
          className={styles.fixedActionsBarButton}
          onClick={() => {
            if (!isValidateLoading) {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'placement',
                event_nom: 'valider',
              });
              onClickValidate();
            }
          }}
          aria-disabled={isValidateLoading}
          aria-busy={isValidateLoading}
          aria-label={t('common.validateButton')}
          variant="primary"
          size="large"
          fullWidth
          disabled={userConseiller?.advisorView ?? false}
        >
          <div className={styles.buttonContent}>
            {isValidateLoading ? (
              <Spinner color="--totem-color-white" diameter={24} />
            ) : (
              <span>{t('common.validateButton')}</span>
            )}
          </div>
        </MtxButton>
      </FixedActionsBar>
    </div>
  );
};

export default ArbitrageSummary;
